import { Colors } from "@blueprintjs/core";
import classNames from "classnames";
import { add, startOfDay } from "date-fns";
import * as React from "react";
import styled from "styled-components";

import { DayInfos } from "../utils/dates";
import { DayCalendarEvent } from "./DayCalendarEvent";
import { TimeSlice } from "./TimeSlice";
import { useCalendarContext } from "../CalendarContext";

export interface IDayColumnProps {
  day: DayInfos;
}

const Container = styled.div`
  position: relative;
  border-bottom: 1px solid black;
  flex: 1;
  &.today {
    background-color: ${Colors.LIGHT_GRAY5};
  }
  &.weekend {
    background-color: ${Colors.LIGHT_GRAY3};
  }
`;

const CustomTimeSlice = styled(TimeSlice)`
  width: 100%;
  border-top: 1px solid ${Colors.LIGHT_GRAY1};
  &.ishour {
    border-top: 1px solid black;
  }
  overflow: hidden;
`;

export const DayColumn: React.FunctionComponent<IDayColumnProps> = ({
  day,
}) => {
  const { dayTimeSlices, sliceHeightInPx, getDayEvents, hourLimit } =
    useCalendarContext();
  const calendarEvents = React.useMemo(
    () => getDayEvents(day),
    [day, getDayEvents]
  );

  const getSliceStartDate = React.useCallback(
    (dateFromStartOfTheDayInMs: number) => {
      return add(startOfDay(day.date), {
        seconds: dateFromStartOfTheDayInMs / 1000,
      });
    },
    [day.date]
  );

  const HoursBetweenDayLimit = React.useMemo(() => {
    if (hourLimit)
      return dayTimeSlices.filter(
        (i) =>
          i.time.hours >= hourLimit.startHour &&
          i.time.hours <= hourLimit.endHour
      );
    return dayTimeSlices;
  }, [dayTimeSlices, hourLimit]);

  return (
    <Container
      className={classNames({ today: day.isToday, weekend: day.isWeekEnd })}
    >
      {HoursBetweenDayLimit.map((h, i) => (
        <CustomTimeSlice
          key={h.time.hours + "_" + i}
          startDate={getSliceStartDate(h.startTimeInMs)}
          style={{ height: sliceHeightInPx }}
          className={classNames({ ishour: h.isHour })}
        />
      ))}

      {calendarEvents.map((e) => (
        <DayCalendarEvent event={e} day={day} key={e.internalId} />
      ))}
    </Container>
  );
};
